import React, { useState, useEffect } from 'react';
import { calendarAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Plus, ChevronLeft, ChevronRight, Loader2, Trash2, Cake, Award, Flag } from 'lucide-react';

const COUNTRIES = ['UAE', 'India', 'UK', 'Singapore', 'Ghana', 'Kenya', 'Uganda', 'Cameroon', 'Mauritius'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarPage = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countryFilter, setCountryFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ name: '', country: 'UAE', date: '', description: '', recurring: false });

  const canManage = ['super_admin', 'hr_admin'].includes(user?.role);

  const fetchEvents = async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    const fmt = (d) => d.toISOString().split('T')[0];
    setLoading(true);
    try {
      const res = await calendarAPI.getEvents(fmt(start), fmt(end), countryFilter === 'all' ? undefined : countryFilter);
      setEvents(res.data);
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); /* eslint-disable-next-line */ }, [currentDate, countryFilter]);

  const handleCreate = async () => {
    if (!newHoliday.name || !newHoliday.date) {
      toast.error('Name and date required');
      return;
    }
    try {
      await calendarAPI.createHoliday(newHoliday);
      toast.success('Holiday added');
      setCreateOpen(false);
      setNewHoliday({ name: '', country: 'UAE', date: '', description: '', recurring: false });
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleDelete = async (id) => {
    if (!id || id.includes('_')) return; // only original holidays (no year suffix)
    try {
      await calendarAPI.deleteHoliday(id);
      toast.success('Deleted');
      fetchEvents();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Build calendar grid
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const eventsByDate = {};
  events.forEach(e => {
    if (!eventsByDate[e.date]) eventsByDate[e.date] = [];
    eventsByDate[e.date].push(e);
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, date: dateStr, events: eventsByDate[dateStr] || [] });
  }

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Calendar</h1>
          <p className="text-muted-foreground">Public holidays, birthdays & work anniversaries</p>
        </div>
        {canManage && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-holiday-btn"><Plus size={16} className="mr-2" />Add Holiday</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Public Holiday</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={newHoliday.name} onChange={(e) => setNewHoliday({...newHoliday, name: e.target.value})} data-testid="holiday-name" /></div>
                <div><Label>Country</Label>
                  <Select value={newHoliday.country} onValueChange={(v) => setNewHoliday({...newHoliday, country: v})}>
                    <SelectTrigger data-testid="holiday-country"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Date</Label><Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({...newHoliday, date: e.target.value})} data-testid="holiday-date" /></div>
                <div><Label>Description</Label><Input value={newHoliday.description} onChange={(e) => setNewHoliday({...newHoliday, description: e.target.value})} /></div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={newHoliday.recurring} onChange={(e) => setNewHoliday({...newHoliday, recurring: e.target.checked})} />
                  Recurring every year on same date
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="save-holiday-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}><ChevronLeft size={18} /></Button>
              <CardTitle className="text-lg w-48 text-center">{MONTHS[month]} {year}</CardTitle>
              <Button variant="outline" size="icon" onClick={() => setCurrentDate(new Date(year, month + 1, 1))}><ChevronRight size={18} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
            </div>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="mx-auto animate-spin h-6 w-6" /> : (
            <div className="grid grid-cols-7 gap-1">
              {DAYS.map(d => <div key={d} className="text-xs font-semibold text-center text-muted-foreground p-2">{d}</div>)}
              {cells.map((c, i) => (
                <div key={i} className={`min-h-[80px] sm:min-h-[100px] border rounded p-1 ${!c ? 'bg-muted/30' : c.date === todayStr ? 'bg-primary/5 border-primary' : ''}`}>
                  {c && (
                    <>
                      <div className="text-xs font-medium mb-1">{c.day}</div>
                      <div className="space-y-0.5">
                        {c.events.slice(0, 3).map(e => (
                          <div key={e.id} className="text-[10px] sm:text-xs px-1 py-0.5 rounded truncate" style={{ backgroundColor: `${e.color}22`, color: e.color }} title={e.title}>
                            {e.title}
                          </div>
                        ))}
                        {c.events.length > 3 && <div className="text-[10px] text-muted-foreground">+{c.events.length - 3} more</div>}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Events This Month ({events.length})</CardTitle></CardHeader>
        <CardContent className="space-y-1 max-h-96 overflow-y-auto">
          {events.length === 0 && <p className="text-sm text-muted-foreground">No events</p>}
          {events.map(e => {
            const Icon = e.type === 'birthday' ? Cake : e.type === 'anniversary' ? Award : Flag;
            return (
              <div key={e.id} className="flex items-center justify-between py-1.5 border-b last:border-0" data-testid={`event-${e.id}`}>
                <div className="flex items-center gap-2">
                  <Icon size={14} style={{ color: e.color }} />
                  <span className="text-sm">{e.title}</span>
                  {e.country && <Badge variant="outline" className="text-xs">{e.country}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{new Date(e.date).toLocaleDateString()}</span>
                  {canManage && e.type === 'holiday' && !e.id.includes('_') && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(e.id)}><Trash2 size={12} /></Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarPage;
